import { useCurrentServerService } from '@affine/core/components/providers/current-server-scope';
import type { ServerConfig } from '@affine/core/modules/cloud';
import { DEFAULT_SELF_HOSTED_SERVER_NAME } from '@affine/core/modules/cloud/server-name';
import { ServerDeploymentType } from '@affine/graphql';
import { Logo1Icon } from '@blocksuite/icons/rc';
import { useLiveData } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import { normalizeOfficialWebsiteUrl } from '../../modules/cloud/stores/server-config';
import * as styles from './index.css';

export const DEFAULT_AFFINE_NAME = 'AFFiNE';
export const DEFAULT_AFFINE_LOGO_URL = '/assets/affine-favicon.png';
export const DEFAULT_AFFINE_FAVICON_URL = '/assets/affine-favicon.png';

export function isValidBrandingUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  const url = value.trim();
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

export type InstanceBranding = {
  name: string;
  subtitle?: string;
  logoUrl: string;
  fallbackLogoUrl: string;
  faviconUrl: string;
  isSelfHosted: boolean;
};

type BrandingConfig = Pick<
  ServerConfig,
  'serverName' | 'subtitle' | 'logoUrl' | 'faviconUrl' | 'type'
>;

export function hasLoadedFaviconConfig(
  config?: Partial<BrandingConfig> | null
): config is Partial<BrandingConfig> & { faviconUrl: string | null } {
  return config?.faviconUrl !== undefined;
}

export function getInstanceBranding(
  config?: Partial<BrandingConfig> | null
): InstanceBranding {
  const isSelfHosted =
    config?.type === ServerDeploymentType.Selfhosted ||
    environment.isSelfHosted;
  const configuredName = config?.serverName?.trim();
  const configuredSubtitle =
    typeof config?.subtitle === 'string' ? config.subtitle.trim() : undefined;
  const name =
    configuredName &&
    !(isSelfHosted && configuredName === DEFAULT_SELF_HOSTED_SERVER_NAME)
      ? configuredName
      : DEFAULT_AFFINE_NAME;
  const fallbackLogoUrl = DEFAULT_AFFINE_LOGO_URL;
  const fallbackFaviconUrl = DEFAULT_AFFINE_FAVICON_URL;
  const configuredLogoUrl = config?.logoUrl?.trim();
  const configuredFaviconUrl = config?.faviconUrl?.trim();

  return {
    name,
    subtitle: configuredSubtitle || undefined,
    logoUrl: isValidBrandingUrl(configuredLogoUrl)
      ? configuredLogoUrl
      : fallbackLogoUrl,
    fallbackLogoUrl,
    faviconUrl: isValidBrandingUrl(configuredFaviconUrl)
      ? configuredFaviconUrl
      : fallbackFaviconUrl,
    isSelfHosted,
  };
}

export function useInstanceBranding() {
  const currentServer = useCurrentServerService();
  const serverConfig = useLiveData(currentServer?.config$);
  return getInstanceBranding(serverConfig);
}

export function useInstanceOfficialWebsiteUrl() {
  const currentServer = useCurrentServerService();
  const serverConfig = useLiveData(currentServer?.config$);
  return normalizeOfficialWebsiteUrl(serverConfig?.officialWebsiteUrl);
}

export function applyInstanceFavicon(
  url: string,
  fallbackUrl = DEFAULT_AFFINE_FAVICON_URL
) {
  if (typeof document === 'undefined') return;

  const safeUrl = isValidBrandingUrl(url) ? url : fallbackUrl;
  const iconLinks = document.querySelectorAll<HTMLLinkElement>(
    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
  );
  const fallback = () => {
    if (safeUrl !== fallbackUrl) {
      iconLinks.forEach(link => {
        link.onerror = null;
        link.href = fallbackUrl;
      });
    }
  };
  if (iconLinks.length) {
    iconLinks.forEach(link => {
      link.onerror = fallback;
      link.href = safeUrl;
    });
    return;
  }

  const link = document.createElement('link');
  link.rel = 'icon';
  link.onerror = fallback;
  link.href = safeUrl;
  document.head.appendChild(link);
}

export const InstanceBrand = () => {
  const branding = useInstanceBranding();
  const [imageUrl, setImageUrl] = useState(branding.logoUrl);

  useEffect(() => {
    setImageUrl(branding.logoUrl);
  }, [branding.logoUrl]);

  return (
    <a href="/" className={styles.link} title={branding.name}>
      {imageUrl ? (
        <img
          className={styles.logo}
          src={imageUrl}
          alt={`${branding.name} logo`}
          onError={() => {
            setImageUrl(current =>
              current === branding.fallbackLogoUrl
                ? undefined
                : branding.fallbackLogoUrl
            );
          }}
        />
      ) : (
        <Logo1Icon className={styles.logo} aria-hidden="true" />
      )}
      <span className={styles.text}>
        <span className={styles.name}>{branding.name}</span>
        {branding.subtitle ? (
          <span className={styles.subtitle}>{branding.subtitle}</span>
        ) : null}
      </span>
    </a>
  );
};
